import * as z from "zod";
import { userFacingError } from "../authorization";
import { zql } from "../generatedDrizzleSchema";
import {
  DEFAULT_PROVIDER_IDS,
  SSO_PROVIDER_ID_PATTERN,
} from "../ssoProviderIds";
import { mutator } from "./mutator";
import { clientGeneratableIdSchema } from "../id";

export const samlMappingSchema = z.object({
  email: z.string().min(1),
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  image: z.string().optional(),
});

const groupMappingEntrySchema = z.object({
  idpGroup: z.string().min(1),
  spaceIds: z.array(z.string().min(1)).min(1),
});

export const groupMappingsSchema = z.object({
  adminGroups: z.array(z.string().min(1)),
  mappings: z.array(groupMappingEntrySchema),
});

export const syncModeSchema = z.enum([
  "off",
  "jit",
  "jit_users_and_groups",
  "scim",
]);

const sharedFields = {
  id: clientGeneratableIdSchema,
  providerId: z.string(),
  domain: z.string(),
  groupMappings: groupMappingsSchema.optional(),
  jitUserProvisioning: z.boolean().optional(),
  syncMode: syncModeSchema.optional(),
};

export const registerSSOProviderSchema = z.discriminatedUnion("protocol", [
  z.object({
    ...sharedFields,
    protocol: z.literal("oidc"),
    issuer: z.string().trim().min(1),
    clientId: z.string(),
    clientSecret: z.string(),
    scopes: z.array(z.string().min(1)).optional(),
  }),
  z.object({
    ...sharedFields,
    protocol: z.literal("saml"),
    // For SAML, the IdP can be configured three ways: pasted metadata XML, a
    // metadata URL we fetch, or the entity ID/sign-on URL/cert entered directly.
    // All are optional here; the server enforces that one source is present and
    // hands metadata XML to samlify (the runtime SAML engine) to parse.
    issuer: z.string().trim().optional(),
    entryPoint: z.string().optional(),
    cert: z.string().optional(),
    metadataUrl: z.string().optional(),
    metadataXml: z.string().optional(),
    mapping: samlMappingSchema.optional(),
  }),
]);

export const registerSSOProvider = mutator({
  redactKeys: ["clientSecret", "cert"],
  schema: registerSSOProviderSchema,
  resolve: {},
  permissions: [
    { permission: "tenant:sso_manage", scopeId: ({ ctx }) => ctx.tenantId },
  ],
  fn: async ({ tx, ctx, args }) => {
    const { userId } = ctx;

    // The provider ID ends up in the callback URLs registered at the IdP, so
    // keep it path-segment-safe.
    if (!new RegExp(`^${SSO_PROVIDER_ID_PATTERN}$`).test(args.providerId)) {
      throw userFacingError(
        "Provider ID must use only letters, numbers, dashes, and underscores"
      );
    }

    // The built-in IdPs resolve these IDs ahead of the database, so a provider
    // registered under one would be unreachable.
    if (DEFAULT_PROVIDER_IDS.has(args.providerId)) {
      throw userFacingError(
        `Provider ID “${args.providerId}” is reserved for a built-in provider`
      );
    }

    const existing = await tx.run(
      zql.ssoProvider.where("tenantId", ctx.tenantId)
    );
    if (existing.some((p) => p.providerId === args.providerId)) {
      throw userFacingError(
        `A provider with ID “${args.providerId}” already exists`
      );
    }

    const now = Date.now();
    await tx.mutate.ssoProvider.insert({
      id: args.id,
      providerId: args.providerId,
      issuer: args.protocol === "saml" ? (args.issuer ?? "") : args.issuer,
      domain: args.domain,
      protocol: args.protocol,
      userId,
      organizationId: null,
      domainVerified: true,
      // The UI sends only syncMode; mirror it into the legacy
      // jitUserProvisioning column so the two stay consistent.
      jitUserProvisioning:
        args.jitUserProvisioning ??
        (args.syncMode ? args.syncMode !== "off" : true),
      groupMappings: null,
      groupClaimName: null,
      // Only a tenant's first provider auto-claims app sign-in; registering
      // another never overrides a deliberate zero-primary state.
      isPrimary: existing.length === 0,
      syncMode: args.syncMode ?? "off",
      createdAt: now,
      updatedAt: now,
      oauthTrustUpdatedAt: now,
      tenantId: ctx.tenantId,
    });
  },
});
